<template>
    <section class="nw" aria-label="Notas do webhook">
        <!-- barra: filtro + ações -->
        <div class="nw-barra">
            <div class="btn-group btn-group-sm nw-filtro" role="group" aria-label="Filtrar notas">
                <button type="button" class="btn" :class="filtro=='todas' ? 'btn-primary' : 'btn-outline-primary'" :aria-pressed="filtro=='todas' ? 'true' : 'false'" @click="filtro='todas'">
                    Todas <span class="nw-filtro__qtd">{{ notas.length }}</span>
                </button>
                <button type="button" class="btn" :class="filtro=='nao_lidas' ? 'btn-primary' : 'btn-outline-primary'" :aria-pressed="filtro=='nao_lidas' ? 'true' : 'false'" @click="filtro='nao_lidas'">
                    Não lidas <span class="nw-filtro__qtd">{{ qtdNaoLidas }}</span>
                </button>
            </div>

            <div class="nw-barra__acoes">
                <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="carregando" @click="atualizar()" title="Buscar notas novas">
                    <b-icon icon="arrow-clockwise" :animation="carregando ? 'spin' : ''"></b-icon> Atualizar
                </button>
                <button type="button" class="btn btn-sm btn-outline-primary" :disabled="qtdNaoLidas==0 || marcandoTodas" @click="marcarTodasLidas()">
                    <b-icon icon="check2-all"></b-icon> Marcar todas como lidas
                </button>
            </div>
        </div>

        <!-- lista -->
        <ol v-if="notasFiltradas.length>0" class="nw-lista">
            <li v-for="nota in notasFiltradas" :key="nota.id" class="nw-item" :class="['nw-item--'+gravidade(nota).chave, { 'nw-item--nao-lida': !estaLida(nota) }]">
                <span class="nw-item__icone" :title="gravidade(nota).rotulo" aria-hidden="true">
                    <b-icon :icon="gravidade(nota).icone"></b-icon>
                </span>

                <div class="nw-item__corpo">
                    <div class="nw-item__topo">
                        <span v-if="!estaLida(nota)" class="nw-item__nova">Nova</span>
                        <h3 class="nw-item__titulo">{{ tituloNota(nota) }}</h3>
                        <span class="ec-status ec-status--rotulo ec-status--neutro nw-item__tipo">
                            <b-icon :icon="tipoNota(nota.tiponotificacao).icone"></b-icon> {{ tipoNota(nota.tiponotificacao).rotulo }}
                        </span>
                    </div>

                    <div class="nw-item__mensagem" :class="{ 'nw-item__mensagem--recolhida': mensagemLonga(nota) && !expandidas[nota.id] }" v-html="sanitizarHtml(nota.mensagem)"></div>
                    <button v-if="mensagemLonga(nota)" type="button" class="btn btn-link btn-sm nw-item__ver-mais" :aria-expanded="expandidas[nota.id] ? 'true' : 'false'" @click="alternarExpandida(nota)">
                        {{ expandidas[nota.id] ? 'Ver menos' : 'Ver mensagem completa' }}
                    </button>

                    <div class="nw-item__meta">
                        <span :title="dataCompleta(nota.created_at)"><b-icon icon="clock"></b-icon> {{ dataRelativa(nota.created_at) }}</span>
                        <span v-if="repetiu(nota)" class="nw-item__repetiu" :title="dataCompleta(nota.updated_at)">
                            <b-icon icon="arrow-repeat"></b-icon> ocorreu de novo {{ dataRelativa(nota.updated_at) }}
                        </span>
                        <span v-if="estaResolvida(nota)" class="nw-item__resolvida" :title="nota.dataleitura ? dataCompleta(nota.dataleitura) : ''">
                            <b-icon icon="check-circle"></b-icon> Resolvida automaticamente: o ERP enviou um retorno mais recente<template v-if="nota.dataleitura"> ({{ dataRelativa(nota.dataleitura) }})</template>
                        </span>
                        <span v-else-if="estaLida(nota)" :title="nota.dataleitura ? dataCompleta(nota.dataleitura) : ''">
                            <b-icon icon="check2"></b-icon> Lida<template v-if="nota.user && nota.user.nome"> por {{ nota.user.nome }}</template><template v-if="nota.dataleitura"> {{ dataRelativa(nota.dataleitura) }}</template>
                        </span>
                    </div>
                </div>

                <div class="nw-item__acoes">
                    <button v-if="!estaLida(nota)" type="button" class="btn btn-sm btn-outline-primary" :disabled="!!marcando[nota.id]" @click="marcarLida(nota)">
                        <b-icon icon="check2"></b-icon> Marcar como lida
                    </button>
                </div>
            </li>
        </ol>

        <!-- estados vazios -->
        <div v-else-if="notas.length==0" class="nw-vazio">
            <b-icon icon="inbox" font-scale="2"></b-icon>
            <p class="nw-vazio__titulo">Nenhuma nota para este webhook</p>
            <p class="nw-vazio__texto">Os avisos do processamento (envio ao ERP, documentos, contrato, matrícula…) aparecem aqui.</p>
        </div>
        <div v-else class="nw-vazio">
            <b-icon icon="check-circle-fill" font-scale="2" class="text-success"></b-icon>
            <p class="nw-vazio__titulo">Tudo lido por aqui</p>
            <p class="nw-vazio__texto">Não há notas pendentes de leitura.</p>
            <button type="button" class="btn btn-sm btn-link" @click="filtro='todas'">Ver todas as notas</button>
        </div>

        <!-- leitura obrigatória: só fecha confirmando a leitura (sem X, ESC ou clique fora).
             O fundo é só esmaecido (estilo global no fim do arquivo). -->
        <b-modal
            :id="'nw-pendentes-'+webhookId"
            size="lg"
            centered
            scrollable
            content-class="shadow-lg"
            no-close-on-backdrop
            no-close-on-esc
            hide-header-close
        >
            <template #modal-title>
                <b-icon icon="bell-fill"></b-icon>
                {{ pendentes.length }} nota{{ pendentes.length>1 ? 's' : '' }} não lida{{ pendentes.length>1 ? 's' : '' }} neste webhook
            </template>

            <p class="nw-modal__intro">Antes de continuar, leia os avisos gerados no processamento deste webhook.</p>

            <ol class="nw-lista">
                <li v-for="nota in pendentes" :key="nota.id" class="nw-item nw-item--nao-lida" :class="'nw-item--'+gravidade(nota).chave">
                    <span class="nw-item__icone" :title="gravidade(nota).rotulo" aria-hidden="true">
                        <b-icon :icon="gravidade(nota).icone"></b-icon>
                    </span>
                    <div class="nw-item__corpo">
                        <div class="nw-item__topo">
                            <h3 class="nw-item__titulo">{{ tituloNota(nota) }}</h3>
                            <span class="ec-status ec-status--rotulo ec-status--neutro nw-item__tipo">
                                <b-icon :icon="tipoNota(nota.tiponotificacao).icone"></b-icon> {{ tipoNota(nota.tiponotificacao).rotulo }}
                            </span>
                        </div>
                        <div class="nw-item__mensagem" v-html="sanitizarHtml(nota.mensagem)"></div>
                        <div class="nw-item__meta">
                            <span :title="dataCompleta(nota.created_at)"><b-icon icon="clock"></b-icon> {{ dataRelativa(nota.created_at) }}</span>
                            <span v-if="repetiu(nota)" class="nw-item__repetiu" :title="dataCompleta(nota.updated_at)">
                                <b-icon icon="arrow-repeat"></b-icon> ocorreu de novo {{ dataRelativa(nota.updated_at) }}
                            </span>
                        </div>
                    </div>
                </li>
            </ol>

            <div v-if="erroConfirmacao" class="alert alert-danger nw-modal__erro" role="alert">
                Não foi possível registrar a leitura: {{ erroConfirmacao }}
            </div>

            <template #modal-footer>
                <div class="nw-modal__rodape">
                    <b-form-checkbox v-model="cienteLeitura" class="nw-modal__ciente">
                        Li e estou ciente destes avisos
                    </b-form-checkbox>
                    <div class="nw-modal__botoes">
                        <button v-if="erroConfirmacao" type="button" class="btn btn-link" @click="fecharPendentes()">Fechar sem confirmar</button>
                        <button type="button" class="btn btn-primary" :disabled="!cienteLeitura || marcandoTodas" @click="confirmarLeituraPendentes()">
                            <b-icon icon="check2-all"></b-icon> {{ marcandoTodas ? 'Registrando...' : 'Confirmar leitura' }}
                        </button>
                    </div>
                </div>
            </template>
        </b-modal>
    </section>
</template>

<script>
// Códigos de tiponotificacao gravados por registrarNota/registrarLog. Indicam o
// processo de origem da nota (não a gravidade).
const TIPOS_NOTA = {
    1: { rotulo: 'Documentos', icone: 'file-earmark-text' },
    6: { rotulo: 'Contrato', icone: 'file-earmark-check' },
    100: { rotulo: 'Nome do inscrito', icone: 'person' },
    200: { rotulo: 'Inscrição', icone: 'person-plus' },
    201: { rotulo: 'Matrícula', icone: 'journal-check' },
    700: { rotulo: 'Processamento', icone: 'gear' },
    754: { rotulo: 'Plano de pagamento', icone: 'credit-card' },
    800: { rotulo: 'Exclusão de documento', icone: 'trash' },
    801: { rotulo: 'Exclusão de documento', icone: 'trash' },
    851: { rotulo: 'Envio de documento', icone: 'upload' },
    891: { rotulo: 'Documentos', icone: 'file-earmark-x' },
    1000: { rotulo: 'Valor da matrícula', icone: 'cash' },
    1500: { rotulo: 'Nº de matrícula', icone: 'hash' },
    2000: { rotulo: 'Retorno do ERP', icone: 'arrow-left-right' },
    5000: { rotulo: 'Atualização cadastral', icone: 'arrow-repeat' },
    10000: { rotulo: 'Inscrição', icone: 'person-plus' },
}

// A nota não guarda a gravidade: ela é estimada pelo texto, só para dar cor e ícone.
const GRAVIDADES = [
    { chave: 'erro', rotulo: 'Erro', icone: 'x-circle-fill', regex: /\berro\b|\berror\b|recusad|falh(a|ou)|inv[aá]lid|n[aã]o foi poss[ií]vel|exception/ },
    { chave: 'aviso', rotulo: 'Atenção', icone: 'exclamation-triangle-fill', regex: /n[aã]o (foi )?encontrad|n[aã]o encontramos|n[aã]o exist|n[aã]o identific|n[aã]o ser[aá]|n[aã]o configur|aguard|falta|difere|opss|aten[cç][aã]o/ },
    { chave: 'sucesso', rotulo: 'Sucesso', icone: 'check-circle-fill', regex: /sucesso|enviad[ao]|finalizad[ao]|efetuad[ao]|atualizad[ao]|sincronizad[ao]/ },
]
const GRAVIDADE_INFO = { chave: 'info', rotulo: 'Informação', icone: 'info-circle-fill' }

export default {
    props: {
        unidadeId: { type: [Number, String], required: true },
        webhookId: { type: [Number, String], required: true },
        notasIniciais: { type: Array, default: () => [] },
    },
    data(){
        return {
            notas: [],
            filtro: 'todas',
            expandidas: {},
            marcando: {},
            marcandoTodas: false,
            carregando: false,
            // popup de leitura obrigatória
            pendentes: [],
            cienteLeitura: false,
            erroConfirmacao: null,
        }
    },
    computed: {
        notasOrdenadas(){
            // última ocorrência primeiro (o aviso repetido atualiza o updated_at)
            return this.notas.slice().sort((a, b) => this.tempo(b.updated_at || b.created_at) - this.tempo(a.updated_at || a.created_at))
        },
        notasFiltradas(){
            return this.filtro == 'nao_lidas' ? this.notasOrdenadas.filter(nota => !this.estaLida(nota)) : this.notasOrdenadas
        },
        qtdNaoLidas(){
            return this.notas.filter(nota => !this.estaLida(nota)).length
        },
        urlBase(){
            return '/api/v1/Access/Unity/Unitie/'+this.unidadeId+'/notificacoes'
        },
    },
    watch: {
        qtdNaoLidas: {
            immediate: true,
            handler(qtd){ this.$emit('nao-lidas', qtd) }
        },
    },
    created(){
        this.notas = (this.notasIniciais || []).map(nota => this.normalizar(nota))
        // abre direto nas pendentes quando houver
        this.filtro = this.qtdNaoLidas > 0 ? 'nao_lidas' : 'todas'
    },
    methods: {
        normalizar(nota){
            return Object.assign({}, nota, { user: nota.user || { id: null, nome: null } })
        },
        // lida: '0' não lida, '1' lida por um usuário, '2' resolvida automaticamente
        estaLida(nota){
            return nota.lida != 0
        },
        estaResolvida(nota){
            return nota.lida == 2
        },
        tipoNota(codigo){
            return TIPOS_NOTA[codigo] || { rotulo: 'Geral', icone: 'bell' }
        },
        textoPuro(html){
            let doc = new DOMParser().parseFromString(String(html || ''), 'text/html')
            return (doc.body.textContent || '').trim()
        },
        gravidade(nota){
            let texto = (this.textoPuro(nota.assunto) + ' ' + this.textoPuro(nota.mensagem)).toLowerCase()
            return GRAVIDADES.find(g => g.regex.test(texto)) || GRAVIDADE_INFO
        },
        tituloNota(nota){
            // "EduConnect CRM - Educacional ERP - Erro Postagem Documento" -> "Erro Postagem Documento"
            let assunto = String(nota.assunto || '').replace(/^((educonnect crm|educacional erp)\s*-\s*)+/i, '').trim()
            if(assunto == '' || /^mensagem autom[aá]tica$/i.test(assunto)){
                return this.tipoNota(nota.tiponotificacao).rotulo
            }
            return assunto
        },
        mensagemLonga(nota){
            let html = String(nota.mensagem || '')
            return this.textoPuro(html).length > 280 || (html.match(/<br\s*\/?>|\n/gi) || []).length > 3
        },
        alternarExpandida(nota){
            this.$set(this.expandidas, nota.id, !this.expandidas[nota.id])
        },
        // as notas trazem HTML simples (<br>, <b>...) mas também textos vindos do ERP/CRM:
        // remove scripts, eventos (on*) e links javascript: antes de renderizar com v-html
        sanitizarHtml(html){
            if(!html){
                return ''
            }
            let doc = new DOMParser().parseFromString(String(html), 'text/html')
            doc.body.querySelectorAll('script, style, iframe, object, embed, link, meta, form, base').forEach(el => el.remove())
            doc.body.querySelectorAll('*').forEach(el => {
                Array.from(el.attributes).forEach(attr => {
                    let nome = attr.name.toLowerCase()
                    let valor = attr.value.replace(/\s/g, '').toLowerCase()
                    if(nome.startsWith('on') || (['href', 'src', 'xlink:href', 'action', 'formaction'].includes(nome) && valor.startsWith('javascript:'))){
                        el.removeAttribute(attr.name)
                    }
                })
            })
            return doc.body.innerHTML
        },

        // ---- datas ----
        // created_at/updated_at chegam em ISO (UTC); dataleitura chega como "AAAA-MM-DD HH:MM:SS" (horário local)
        tempo(valor){
            if(!valor){
                return 0
            }
            let data = new Date(String(valor).replace(' ', 'T'))
            return isNaN(data.getTime()) ? 0 : data.getTime()
        },
        dataCompleta(valor){
            let tempo = this.tempo(valor)
            return tempo ? new Date(tempo).toLocaleString('pt-BR') : ''
        },
        dataRelativa(valor){
            let tempo = this.tempo(valor)
            if(!tempo){
                return ''
            }
            let data = new Date(tempo)
            let minutos = Math.round((Date.now() - tempo) / 60000)
            let hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
            let hoje = new Date(); hoje.setHours(0, 0, 0, 0)
            let ontem = new Date(hoje.getTime() - 86400000)

            if(minutos < 1){ return 'agora' }
            if(minutos < 60){ return 'há ' + minutos + ' min' }
            if(data >= hoje){ return 'hoje às ' + hora }
            if(data >= ontem){ return 'ontem às ' + hora }
            return 'em ' + data.toLocaleDateString('pt-BR') + ' às ' + hora
        },
        repetiu(nota){
            // o aviso repetido atualiza o updated_at; leituras antigas também mexiam nele,
            // por isso ignora quando o updated_at coincide com a data da leitura
            let criada = this.tempo(nota.created_at)
            let atualizada = this.tempo(nota.updated_at)
            let lida = this.tempo(nota.dataleitura)
            return atualizada - criada > 60000 && (!lida || Math.abs(atualizada - lida) > 5000)
        },

        // ---- ações ----
        avisar(mensagem, variante = 'danger'){
            this.$bvToast.toast(mensagem, {
                title: variante == 'danger' ? 'Não foi possível concluir' : 'Notas do webhook',
                variant: variante,
                solid: true,
                autoHideDelay: 5000,
            })
        },
        mensagemErro(error){
            return (error && error.response && error.response.data && error.response.data.mensagem) || 'Verifique sua conexão e tente novamente.'
        },
        substituirNotas(notas){
            this.notas = (notas || []).map(nota => this.normalizar(nota))
        },
        marcarLida(nota){
            this.$set(this.marcando, nota.id, true)
            axios.post(this.urlBase+'/'+nota.id)
                .then(response => {
                    if(response.data && response.data.sucesso){
                        let indice = this.notas.findIndex(n => n.id == nota.id)
                        if(indice >= 0){
                            this.$set(this.notas, indice, this.normalizar(response.data.nota))
                        }
                    }
                })
                .catch(error => this.avisar(this.mensagemErro(error)))
                .finally(() => this.$delete(this.marcando, nota.id))
        },
        // resolve true quando a leitura foi registrada
        registrarTodasLidas(){
            this.marcandoTodas = true
            return axios.post(this.urlBase+'/webhook/'+this.webhookId+'/lidas')
                .then(response => {
                    if(response.data && response.data.sucesso){
                        this.substituirNotas(response.data.notas)
                        return true
                    }
                    return Promise.reject(null)
                })
                .finally(() => { this.marcandoTodas = false })
        },
        marcarTodasLidas(){
            this.registrarTodasLidas()
                .then(() => this.avisar('Todas as notas foram marcadas como lidas.', 'success'))
                .catch(error => this.avisar(this.mensagemErro(error)))
        },

        // ---- popup de leitura obrigatória ----
        // Abre o popup com as notas não lidas, se houver. Retorna se abriu.
        exigirLeituraPendentes(){
            this.pendentes = this.notasOrdenadas.filter(nota => !this.estaLida(nota))
            if(this.pendentes.length == 0){
                return false
            }
            this.cienteLeitura = false
            this.erroConfirmacao = null
            this.$bvModal.show('nw-pendentes-'+this.webhookId)
            return true
        },
        confirmarLeituraPendentes(){
            this.erroConfirmacao = null
            this.registrarTodasLidas()
                .then(() => {
                    this.fecharPendentes()
                    this.avisar('Leitura das notas registrada.', 'success')
                })
                .catch(error => { this.erroConfirmacao = this.mensagemErro(error) })
        },
        fecharPendentes(){
            this.$bvModal.hide('nw-pendentes-'+this.webhookId)
        },
        atualizar(){
            this.carregando = true
            axios.get(this.urlBase+'/webhook/'+this.webhookId)
                .then(response => {
                    if(response.data && response.data.sucesso){
                        this.substituirNotas(response.data.notas)
                    }
                })
                .catch(error => this.avisar(this.mensagemErro(error)))
                .finally(() => { this.carregando = false })
        },
    },
}
</script>

<style lang="scss" scoped>
@import '../../../sass/variables';

.nw-barra {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: .75rem;
    margin-bottom: 1rem;
}

.nw-barra__acoes {
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
}

.nw-filtro__qtd {
    display: inline-block;
    min-width: 1.4rem;
    margin-left: .3rem;
    padding: 0 .4rem;
    border-radius: 999px;
    font-size: .72rem;
    font-weight: 700;
    background: rgba(#fff, .25);
}

.btn-outline-primary .nw-filtro__qtd { background: rgba($ec-petroleo, .1); }

.nw-lista {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: .6rem;
}

.nw-item {
    --nw-cor: #{$ec-turquesa};
    --nw-fundo: #{rgba($ec-turquesa, .1)};

    display: flex;
    align-items: flex-start;
    gap: .85rem;
    padding: .9rem 1rem;
    background: #fff;
    border: 1px solid $ec-borda;
    border-left: 4px solid transparent;
    border-radius: $border-radius-lg;
    transition: box-shadow .15s ease, background-color .15s ease;

    &:hover { box-shadow: $box-shadow-sm; }

    &--erro { --nw-cor: #b42318; --nw-fundo: #fde4e4; }
    &--aviso { --nw-cor: #8a5a00; --nw-fundo: #fff3d6; }
    &--sucesso { --nw-cor: #1d7a46; --nw-fundo: #{$ec-menta}; }

    // não lida: faixa colorida e fundo levemente destacado
    &--nao-lida {
        border-left-color: var(--nw-cor);
        background: rgba($ec-turquesa, .03);
    }
}

.nw-item__icone {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 50%;
    font-size: 1rem;
    color: var(--nw-cor);
    background: var(--nw-fundo);
}

.nw-item__corpo {
    flex: 1;
    min-width: 0;
}

.nw-item__topo {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: .35rem .5rem;
    margin-bottom: .25rem;
}

.nw-item__titulo {
    margin: 0;
    font-size: .95rem;
    font-weight: 600;
    color: $ec-texto;
    overflow-wrap: anywhere;

    .nw-item--nao-lida & { font-weight: 700; }
}

.nw-item__nova {
    padding: .05rem .45rem;
    border-radius: 999px;
    font-size: .65rem;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #fff;
    background: var(--nw-cor);
}

.nw-item__tipo { font-weight: 600; }

.nw-item__mensagem {
    font-size: .875rem;
    color: $ec-texto;
    overflow-wrap: anywhere;
    white-space: pre-line;

    ::v-deep p { margin: 0; }

    &--recolhida {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
}

.nw-item__ver-mais {
    padding: 0;
    font-size: .8rem;
}

.nw-item__meta {
    display: flex;
    flex-wrap: wrap;
    gap: .25rem 1rem;
    margin-top: .45rem;
    font-size: .75rem;
    color: $ec-texto-suave;
}

.nw-item__repetiu {
    font-weight: 600;
    color: var(--nw-cor);
}

.nw-item__resolvida {
    font-weight: 600;
    color: #1d7a46;
}

.nw-item__acoes {
    flex-shrink: 0;
    align-self: center;
}

.nw-vazio {
    padding: 2rem 1rem;
    text-align: center;
    color: $ec-texto-suave;
    background: $ec-fundo;
    border: 1px dashed $ec-borda;
    border-radius: $border-radius-lg;
}

.nw-vazio__titulo {
    margin: .6rem 0 .15rem;
    font-weight: 700;
    color: $ec-texto;
}

.nw-vazio__texto {
    margin: 0;
    font-size: .85rem;
}

// ---- popup de leitura obrigatória ----
.nw-modal__intro {
    margin-bottom: 1rem !important;
    color: $ec-texto-suave;
}

.nw-modal__erro { margin: 1rem 0 0; }

.nw-modal__rodape {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: .75rem;
    width: 100%;
}

.nw-modal__ciente { font-weight: 600; }

.nw-modal__botoes {
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
    margin-left: auto;
}

@media (max-width: 575.98px) {
    .nw-item { flex-wrap: wrap; padding: .8rem; }
    .nw-item__corpo { flex-basis: calc(100% - 3.1rem); }
    .nw-item__acoes { width: 100%; padding-left: 3.05rem; }
    .nw-barra__acoes { width: 100%; }
}
</style>

<style lang="scss">
@import '../../../sass/variables';

// O b-modal leva o fundo para fora do componente (o scoped não alcança).
// Só o popup de notas pendentes: fundo esmaecido em vez do escuro padrão.
.modal-backdrop[id^="nw-pendentes-"][id$="__BV_modal_backdrop_"] {
    background-color: $ec-petroleo;

    &.show { opacity: .18; }
}
</style>
